import { OFFICE_EXTS } from "@/src/constants/files-extensions";
import { useTheme } from "@/src/styles/theme-provider";
import {
  getFileExtension,
  getFilename,
  getMimeType,
  isImageFile,
} from "@/src/utils/attachment.utils";
import ThemedModal from "@/src/utils/theme-modal";
import * as FileSystem from "expo-file-system/legacy";
import * as IntentLauncher from "expo-intent-launcher";
import * as Sharing from "expo-sharing";
import {
  CloseCircle,
  Document,
  DocumentDownload,
  Eye,
} from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Alert,
  Image,
  Platform,
  Pressable,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import ScButton from "./button";

export type Attachment = Readonly<{
  url: string;
  type: "image" | "file";
}>;

type AttachmentViewerProps = Readonly<{
  attachments: Attachment[];
  previewHeight?: number;
}>;

export const getIconColor = (ext: string): string =>
  (
    ({
      pdf: "--color-crimson-default",
      doc: "--color-primary-default",
      docx: "--color-primary-default",
      xls: "--color-jade-default",
      xlsx: "--color-jade-default",
      ppt: "--color-coral-default",
      pptx: "--color-coral-default",
      txt: "--color-slate-default",
      csv: "--color-slate-default",
    }) as Record<string, string>
  )[ext] ?? "--color-slate-default";

export const getIconBg = (ext: string): string =>
  (
    ({
      pdf: "--color-blush-default",
      doc: "--color-primary-2",
      docx: "--color-primary-2",
      xls: "--color-sage-default",
      xlsx: "--color-sage-default",
      ppt: "--color-wheat-default",
      pptx: "--color-wheat-default",
      txt: "--color-lavender-default",
      csv: "--color-lavender-default",
    }) as Record<string, string>
  )[ext] ?? "--color-obsidian-default";

async function fetchAsBlob(url: string, mimeType: string): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Fetch failed: HTTP ${res.status}`);
  const raw = await res.blob();
  return new Blob([raw], { type: mimeType });
}

async function webOpenFile(url: string, ext: string): Promise<void> {
  if (OFFICE_EXTS.includes(ext)) {
    const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    window.open(viewerUrl, "_blank", "noopener,noreferrer");
    return;
  }
  if (["txt", "csv"].includes(ext)) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  const mimeType = getMimeType(ext);
  const blob = await fetchAsBlob(url, mimeType);
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
}

async function webDownloadFile(
  url: string,
  filename: string,
  ext: string,
): Promise<void> {
  const mimeType = getMimeType(ext);
  const blob = await fetchAsBlob(url, mimeType);
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
}

async function nativeGetCachedUri(
  url: string,
  filename: string,
): Promise<string> {
  const localUri = `${FileSystem.cacheDirectory}${filename}`;
  const info = await FileSystem.getInfoAsync(localUri);
  if (info.exists) return localUri;
  const result = await FileSystem.downloadAsync(url, localUri);
  if (result.status !== 200) throw new Error(`HTTP ${result.status}`);
  return result.uri;
}

async function nativeOpenFile(localUri: string, ext: string): Promise<void> {
  const mimeType = getMimeType(ext);
  if (Platform.OS === "android") {
    const contentUri = await FileSystem.getContentUriAsync(localUri);
    await IntentLauncher.startActivityAsync("android.intent.action.VIEW", {
      data: contentUri,
      flags: 1,
      type: mimeType,
    });
  } else {
    await Sharing.shareAsync(localUri, {
      mimeType,
      UTI: mimeType,
      dialogTitle: "Open with…",
    });
  }
}

async function nativeSaveFile(
  localUri: string,
  filename: string,
  ext: string,
): Promise<void> {
  const mimeType = getMimeType(ext);
  if (Platform.OS === "android") {
    const perm =
      await FileSystem.StorageAccessFramework.requestDirectoryPermissionsAsync();
    if (perm.granted) {
      const base64 = await FileSystem.readAsStringAsync(localUri, {
        encoding: FileSystem.EncodingType.Base64,
      });
      const dest = await FileSystem.StorageAccessFramework.createFileAsync(
        perm.directoryUri,
        filename,
        mimeType,
      );
      await FileSystem.writeAsStringAsync(dest, base64, {
        encoding: FileSystem.EncodingType.Base64,
      });
      return;
    } else {
      await Sharing.shareAsync(localUri, {
        mimeType,
        dialogTitle: "Save file",
      });
    }
  } else {
    await Sharing.shareAsync(localUri, {
      mimeType,
      UTI: mimeType,
      dialogTitle: "Save file",
    });
  }
}

async function handleOpenFile(url: string, filename: string, ext: string) {
  if (Platform.OS === "web") {
    await webOpenFile(url, ext);
  } else {
    const localUri = await nativeGetCachedUri(url, filename);
    await nativeOpenFile(localUri, ext);
  }
}

async function handleSaveFile(
  url: string,
  filename: string,
  ext: string,
): Promise<boolean> {
  if (Platform.OS === "web") {
    await webDownloadFile(url, filename, ext);
    return false;
  }
  const localUri = await nativeGetCachedUri(url, filename);
  if (Platform.OS === "android") {
    const perm =
      await FileSystem.StorageAccessFramework.requestDirectoryPermissionsAsync();
    if (perm.granted) {
      const mimeType = getMimeType(ext);
      const base64 = await FileSystem.readAsStringAsync(localUri, {
        encoding: FileSystem.EncodingType.Base64,
      });
      const dest = await FileSystem.StorageAccessFramework.createFileAsync(
        perm.directoryUri,
        filename,
        mimeType,
      );
      await FileSystem.writeAsStringAsync(dest, base64, {
        encoding: FileSystem.EncodingType.Base64,
      });
      return true;
    } else {
      const mimeType = getMimeType(ext);
      await Sharing.shareAsync(localUri, {
        mimeType,
        dialogTitle: "Save file",
      });
      return false;
    }
  } else {
    await nativeSaveFile(localUri, filename, ext);
    return false;
  }
}

function ImagePreviewModal({
  attachment,
  visible,
  onClose,
}: {
  attachment: Attachment | null;
  visible: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [saving, setSaving] = useState(false);

  if (!attachment) return null;

  const filename = getFilename(attachment.url);
  const ext = getFileExtension(attachment.url);

  const onSave = async () => {
    try {
      setSaving(true);
      const savedToFolder = await handleSaveFile(attachment.url, filename, ext);
      if (savedToFolder) {
        Alert.alert(t("SUCCESS"), t("SUCCESS_FILE_SAVED", { filename }));
      }
    } catch {
      if (Platform.OS !== "web") {
        Alert.alert(t("ERROR"), t("ERROR_IMAGE_SAVE_FAILED"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ThemedModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View className="flex-1 bg-black/90">
        <View className="flex-row items-center justify-between px-5 pt-12 pb-3">
          <Text
            className="text-white text-sm font-semibold flex-1 mr-4"
            numberOfLines={1}
          >
            {filename}
          </Text>
          <View className="flex-row gap-3">
            <TouchableOpacity
              onPress={onSave}
              disabled={saving}
              className="bg-obsidean rounded-full p-2.5 hover:bg-ash-3"
            >
              {saving ? (
                <ActivityIndicator
                  size={16}
                  color={themeVars["--color-onyx-default"] as string}
                />
              ) : (
                <DocumentDownload size={18} className="text-white" />
              )}
            </TouchableOpacity>
            <TouchableOpacity
              onPress={onClose}
              className="bg-obsidean rounded-full p-2.5 hover:bg-ash-3"
            >
              <CloseCircle size={18} className="text-white" />
            </TouchableOpacity>
          </View>
        </View>

        <Pressable
          className="flex-1 items-center justify-center px-4"
          onPress={onClose}
        >
          <Pressable
            onPress={(event) => event.stopPropagation()}
            className="w-full h-full"
          >
            <Image
              source={{ uri: attachment.url }}
              style={{ width: "100%", height: "100%" }}
              resizeMode="contain"
              className="rounded-2xl"
            />
          </Pressable>
        </Pressable>

        <View className="items-center pb-10">
          <Text className="text-chalk/30 text-xs">
            {t("HINT_TAP_OUTSIDE_CLOSE")}
          </Text>
        </View>
      </View>
    </ThemedModal>
  );
}

export function FileActionSheet({
  attachment,
  visible,
  onClose,
}: {
  attachment: Attachment | null;
  visible: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [openLoading, setOpenLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  if (!attachment) return null;

  const filename = getFilename(attachment.url);
  const ext = getFileExtension(attachment.url);
  const iconColor = themeVars[getIconColor(ext)] as string;
  const iconBg = themeVars[getIconBg(ext)] as string;
  const busy = openLoading || downloadLoading;
  const isWeb = Platform.OS === "web";

  const onOpen = async () => {
    try {
      setOpenLoading(true);
      await handleOpenFile(attachment.url, filename, ext);
      if (isWeb) onClose();
    } catch {
      if (!isWeb) {
        Alert.alert(
          t("ERROR_CANNOT_OPEN_FILE"),
          t("ERROR_CANNOT_OPEN_FILE_DESC"),
        );
      }
    } finally {
      setOpenLoading(false);
    }
  };

  const onDownload = async () => {
    try {
      setDownloadLoading(true);
      const savedToFolder = await handleSaveFile(attachment.url, filename, ext);
      if (savedToFolder) {
        Alert.alert(t("SUCCESS"), t("SUCCESS_FILE_SAVED", { filename }));
      }
      if (isWeb) onClose();
    } catch {
      if (!isWeb) {
        Alert.alert(
          t("ERROR_DOWNLOAD_FAILED"),
          t("ERROR_DOWNLOAD_FAILED_DESC"),
        );
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  const openSubtitle = isWeb
    ? OFFICE_EXTS.includes(ext)
      ? t("HINT_OPEN_FILE_WEB_OFFICE")
      : t("HINT_OPEN_FILE_WEB")
    : Platform.OS === "android"
      ? t("HINT_OPEN_FILE_ANDROID")
      : t("HINT_OPEN_FILE_IOS");

  const downloadSubtitle = isWeb
    ? t("HINT_DOWNLOAD_WEB")
    : Platform.OS === "android"
      ? t("HINT_DOWNLOAD_ANDROID")
      : t("HINT_DOWNLOAD_IOS");

  return (
    <ThemedModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable className="flex-1 bg-black/50 items-center justify-center px-6">
        <Pressable
          onPress={(event) => event.stopPropagation()}
          className="bg-chalk rounded-2xl px-6 pt-6 pb-6 w-full max-w-sm md:w-[26%]"
          style={{ elevation: 24 }}
        >
          <View className="w-10 h-1 bg-ash-3 rounded-full self-center mb-5" />

          <View className="flex-row items-center gap-4 mb-5">
            <View
              className="w-14 h-14 rounded-2xl items-center justify-center"
              style={{ backgroundColor: iconBg }}
            >
              <Document size={30} color={iconColor} />
            </View>
            <View className="flex-1">
              <Text
                className="text-base font-semibold text-onyx"
                numberOfLines={2}
              >
                {filename}
              </Text>
              <Text className="text-xs text-slate uppercase mt-0.5 font-medium tracking-wide">
                {`${ext} ${t("LABEL_FILE_DOCUMENT")}`}
              </Text>
            </View>
          </View>

          <View className="h-px bg-ash-3 mb-5" />

          <View className="gap-3">
            <TouchableOpacity
              onPress={onOpen}
              disabled={busy}
              className="flex-row items-center gap-3 bg-primary rounded-2xl px-5 py-4 active:opacity-80 hover:opacity-90"
            >
              {openLoading ? (
                <ActivityIndicator size={20} color="white" />
              ) : (
                <Eye size={20} color="white" />
              )}
              <View className="flex-1">
                <Text className="text-white font-semibold text-base">
                  {t("BTN_OPEN_FILE")}
                </Text>
                <Text className="text-white/80 text-xs mt-0.5">
                  {openSubtitle}
                </Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={onDownload}
              disabled={busy}
              className="flex-row items-center gap-3 bg-obsidian rounded-2xl px-5 py-4 active:opacity-80 hover:bg-ash-3"
            >
              {downloadLoading ? (
                <ActivityIndicator
                  size={20}
                  color={themeVars["--color-onyx-default"] as string}
                />
              ) : (
                <DocumentDownload
                  size={20}
                  color={themeVars["--color-onyx-default"] as string}
                />
              )}
              <View className="flex-1">
                <Text className="text-onyx font-semibold text-base">
                  {t("BTN_DOWNLOAD")}
                </Text>
                <Text className="text-slate text-xs mt-0.5">
                  {downloadSubtitle}
                </Text>
              </View>
            </TouchableOpacity>
            <ScButton
              className="text-slate font-medium text-base"
              text={t("BTN_CANCEL")}
              onPress={onClose}
              mode="outlined"
              disabled={busy}
            />
          </View>
        </Pressable>
      </Pressable>
    </ThemedModal>
  );
}

function AttachmentCard({
  attachment,
  previewHeight,
  onPress,
}: {
  attachment: Attachment;
  previewHeight: number;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const filename = getFilename(attachment.url);
  const isImage = isImageFile(attachment.url);
  const ext = getFileExtension(attachment.url);

  return (
    <Pressable
      onPress={onPress}
      style={{ flexBasis: "31.5%", flexGrow: 1, maxWidth: "33%" }}
      className="bg-chalk rounded-xl border border-ash-3 overflow-hidden active:opacity-70 hover:bg-ash-3"
    >
      <View
        className="w-full items-center justify-center overflow-hidden"
        style={{
          height: previewHeight,
          backgroundColor: isImage
            ? (themeVars["--color-ash-default"] as string)
            : (themeVars[getIconBg(ext)] as string),
        }}
      >
        {isImage ? (
          <Image
            source={{ uri: attachment.url }}
            style={{ width: "100%", height: "100%" }}
            resizeMode="contain"
          />
        ) : (
          <Document size={48} color={themeVars[getIconColor(ext)] as string} />
        )}
      </View>

      <View className="px-3 py-2.5">
        <Text className="text-sm font-medium text-onyx" numberOfLines={1}>
          {filename}
        </Text>
        <View className="flex-row items-center justify-between mt-1">
          <Text className="text-xs text-slate uppercase font-medium tracking-wide">
            {ext}
          </Text>
          <View className="bg-obsidian rounded-full px-2 py-0.5">
            <Text className="text-xs text-slate font-medium">
              {isImage ? t("LABEL_PREVIEW") : t("LABEL_OPEN")}
            </Text>
          </View>
        </View>
      </View>
    </Pressable>
  );
}

export default function AttachmentViewer({
  attachments,
  previewHeight = 140,
}: AttachmentViewerProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (!attachments || attachments.length === 0) return null;

  const selected = selectedIndex !== null ? attachments[selectedIndex] : null;
  const selectedIsImage = selected ? isImageFile(selected.url) : false;

  return (
    <>
      <View className="flex-row flex-wrap" style={{ gap: 12 }}>
        {attachments.map((attachment, index) => (
          <AttachmentCard
            key={`${attachment.url}-${index}`}
            attachment={attachment}
            previewHeight={previewHeight}
            onPress={() => setSelectedIndex(index)}
          />
        ))}
      </View>

      <ImagePreviewModal
        attachment={selectedIsImage ? selected : null}
        visible={selectedIndex !== null && selectedIsImage}
        onClose={() => setSelectedIndex(null)}
      />

      <FileActionSheet
        attachment={!selectedIsImage ? selected : null}
        visible={selectedIndex !== null && !selectedIsImage}
        onClose={() => setSelectedIndex(null)}
      />
    </>
  );
}
